import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useAbility } from '@features/authorization';
import { useCrmCustomerProfile } from '@features/crm-customer-profile';

import {
  crmBtProgramLogDefaultFlowDbValue,
  CrmTrainingProgramLogs,
  type CrmBtProgramLogFilters,
} from '@features/crm-training-program-logs';
import {
  useCrmCustomerRecord,
  useCrmCustomerRecordRouteGuard,
} from '@widgets/crm-customer-record-shell';

export const Route = createFileRoute(
  '/$locale/_authenticated/crm/user-profiles/$userProfileId/training/'
)({ component: CrmCustomerTrainingRoute });

function CrmCustomerTrainingRoute() {
  const navigate = Route.useNavigate();
  const ability = useAbility();
  const { userProfileId: customerId, locale } = Route.useParams();
  const search = Route.useSearch();
  const { customer, refreshAuthorization, viewerAccessScope } = useCrmCustomerRecord();
  const profile = useCrmCustomerProfile(viewerAccessScope, customerId);
  const fallback = useCrmCustomerRecordRouteGuard('training');
  if (fallback !== null)
    return (
      <Navigate
        to={fallback.route}
        params={{ userProfileId: customerId, locale }}
        search={search}
        replace
      />
    );
  const trainingFlow = search.trainingFlow;
  // v1.1.21 emitted the stale legacy string into URLs. Replace it before rendering so the selected
  // value is exactly the value returned by the current Flow catalogue, not a client-side label fix.
  if (trainingFlow === undefined || trainingFlow === 'standard')
    return (
      <Navigate
        to="/$locale/crm/user-profiles/$userProfileId/training"
        params={{ userProfileId: customerId, locale }}
        search={(current) => ({ ...current, trainingFlow: crmBtProgramLogDefaultFlowDbValue })}
        replace
      />
    );
  const filters: CrmBtProgramLogFilters = {
    ...(trainingFlow === 'all' ? {} : { flows: [trainingFlow] }),
    ...(search.trainingQ ? { q: search.trainingQ } : {}),
    ...(search.trainingDateStartedFrom ? { startedFrom: search.trainingDateStartedFrom } : {}),
    ...(search.trainingDateStartedTo ? { startedTo: search.trainingDateStartedTo } : {}),
    ...(search.trainingProgramIds ? { programIds: search.trainingProgramIds } : {}),
    ...(search.trainingStatus ? { statuses: [search.trainingStatus] } : {}),
  };
  return (
    <CrmTrainingProgramLogs
      cursor={search.trainingCursor}
      cursorHistory={search.trainingCursorHistory}
      locale={locale}
      filters={filters}
      profileRef={customer.customerId}
      currentBtLevelId={profile.data?.data.profile?.btLevel?.id ?? null}
      viewerAccessScope={viewerAccessScope}
      deletionOperationId={search.trainingDeletionOperationId}
      deletedLogId={search.trainingDeletedLogId}
      removalOperationId={search.trainingRemovalOperationId}
      removedProgramId={search.trainingRemovedProgramId}
      onRemoveProgramAccepted={(operationId, trainingProgramId) =>
        void navigate({
          replace: true,
          search: (current) => ({
            ...current,
            trainingRemovalOperationId: operationId,
            trainingRemovedProgramId: trainingProgramId,
            trainingDeletionOperationId: undefined,
            trainingDeletedLogId: undefined,
          }),
        })
      }
      onAuthorizationFailure={refreshAuthorization}
      onFiltersChange={(next) =>
        void navigate({
          replace: true,
          search: (current) => ({
            ...current,
            trainingQ: next.q,
            trainingDateStartedFrom: next.startedFrom,
            trainingDateStartedTo: next.startedTo,
            trainingCursor: undefined,
            trainingCursorHistory: undefined,
            trainingFlow:
              next.flows === undefined
                ? crmBtProgramLogDefaultFlowDbValue
                : (next.flows[0] ?? 'all'),
            trainingProgramIds: next.programIds,
            trainingStatus: next.statuses?.[0],
          }),
        })
      }
      onCursorChange={(trainingCursor, trainingCursorHistory) =>
        void navigate({
          replace: true,
          search: (current) => ({
            ...current,
            trainingCursor,
            trainingCursorHistory:
              trainingCursorHistory === undefined || trainingCursorHistory.length === 0
                ? undefined
                : trainingCursorHistory,
          }),
        })
      }
      onOpenLog={(programLogId) =>
        navigate({
          to: '/$locale/crm/user-profiles/$userProfileId/training/$programLogId',
          params: { userProfileId: customerId, locale, programLogId },
          search,
        })
      }
      onEditLog={
        ability.can('update', 'CrmCustomerProgramLog')
          ? (programLogId) =>
              void navigate({
                to: '/$locale/crm/user-profiles/$userProfileId/training/$programLogId',
                params: { userProfileId: customerId, locale, programLogId },
                search: { ...search, edit: true },
              })
          : undefined
      }
      onDeleteLog={(programLogId) =>
        void navigate({
          to: '/$locale/crm/user-profiles/$userProfileId/training/$programLogId',
          params: { userProfileId: customerId, locale, programLogId },
          search: { ...search, delete: true },
        })
      }
    />
  );
}
